import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import axios from 'axios';
import api from '../utils/axios';

const AIAgentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const chatMessagesRef = useRef(null);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [messageLikes, setMessageLikes] = useState({}); // Track likes for each message
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      type: 'agent',
      content: 'Hello! I\'m your AI assistant. How can I help you today?',
      timestamp: '2:30 PM'
    }
  ]);

  // Get user ID from localStorage
  const userId = localStorage.getItem('userId') || 'user123';

  // Per-agent API configuration (override via env vars if needed)
  const agentApiConfig = {
    '1': {
      baseUrl: process.env.REACT_APP_AGENT_1_API_BASE || 'https://agentbclick.com/customer-service/api'
    },
    '2': {
      baseUrl: process.env.REACT_APP_AGENT_2_API_BASE || 'https://agentbclick.com/rh-agent/api'
    }
  };

  const currentAgentApi = agentApiConfig[id] || agentApiConfig['1'];

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Mock agent data
  const agent = {
    id: id,
    name: id === '1' ? 'Customer Support agent' : id === '2' ? 'RH agent' : 'Agent',
    description: id === '1'
      ? 'AI-powered customer support for handling inquiries and resolving issues'
      : id === '2'
      ? 'AI-powered RH agent for HR-related assistance and employee support'
      : 'AI agent',
    status: 'active',
    type: 'chatbot',
    lastActive: '1 hour ago',
    performance: 94,
    avatar: '🤖',
    rating: 0, // Placeholder, will be updated by comments
    totalReviews: 0, // Placeholder, will be updated by comments
    responseTime: '2.3s',
    accuracy: '94%'
  };

  // Fetch comments on component mount
  useEffect(() => {
    fetchComments();
    fetchChatHistory();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate review stats when comments change
  useEffect(() => {
    // Update agent data with new stats
    agent.rating = 0; // Placeholder, will be updated by comments
    agent.totalReviews = 0; // Placeholder, will be updated by comments
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChatHistory = async () => {
    if (chatHistoryLoaded) return; // Only load once
    
    try {
      const response = await axios.get(`${currentAgentApi.baseUrl}/chat/history/${userId}?limit=50`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.messages && response.data.messages.length > 0) {
        // Convert API response to chat message format
        const historyMessages = response.data.messages.map((msg, index) => ({
          id: index + 1,
          type: msg.sender === 'user' ? 'user' : 'agent',
          content: msg.message,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          originalId: msg._id,
          sessionId: msg.session_id,
          audioUrl: msg.audio_url,
          mediaType: msg.media_type
        }));

        setChatMessages(historyMessages);
        
        // Scroll to bottom after loading history
        setTimeout(() => scrollToBottom(), 200);
      }
      setChatHistoryLoaded(true);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setChatHistoryLoaded(true); // Mark as loaded even if failed to prevent retries
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/api/comments/${id}`);
      if (response.data.success) {
        // Update agent data with new stats
        agent.rating = response.data.averageRating || 0;
        agent.totalReviews = response.data.totalReviews || 0;
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Fallback to mock data if API fails
      agent.rating = 0; // Placeholder, will be updated by comments
      agent.totalReviews = 0; // Placeholder, will be updated by comments
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() && !isLoading) {
      const userMessage = {
        id: chatMessages.length + 1,
        type: 'user',
        content: message,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      // Add user message to chat
      setChatMessages(prev => [...prev, userMessage]);
      const currentMessage = message;
      setMessage('');
      setIsLoading(true);

      // Scroll to bottom after user message
      setTimeout(() => scrollToBottom(), 100);

      try {
        // Send message to agent-specific API
        const response = await axios.post(`${currentAgentApi.baseUrl}/chat`, {
          user_id: userId,
          message: currentMessage
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        // Add agent response to chat
        const agentResponse = {
          id: chatMessages.length + 2,
          type: 'agent',
          content: response.data.agent_response,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          originalId: response.data.message_id || `temp_${Date.now()}_${Math.random()}`,
          sessionId: response.data.session_id || 'default_session'
        };

        setChatMessages(prev => [...prev, agentResponse]);
        
        // Scroll to bottom after agent response
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        console.error('Error sending message:', error);
        
        // Add error message to chat
        const errorResponse = {
          id: chatMessages.length + 2,
          type: 'agent',
          content: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isError: true,
          originalId: `error_${Date.now()}_${Math.random()}`,
          sessionId: 'default_session'
        };

        setChatMessages(prev => [...prev, errorResponse]);
        
        // Scroll to bottom after error message
        setTimeout(() => scrollToBottom(), 100);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLike = async (messageId, isLike) => {
    try {
      const token = localStorage.getItem('token');
      
      // Find the message to get its content and session_id
      const message = chatMessages.find(msg => msg.originalId === messageId);
      if (!message) {
        console.error('Message not found for like/dislike, messageId:', messageId);
        console.log('Available messages:', chatMessages.map(m => ({ id: m.id, originalId: m.originalId })));
        return;
      }

      if (!messageId) {
        console.error('Message ID is undefined');
        return;
      }

      console.log('Sending like/dislike request:', {
        message_id: messageId,
        session_id: message.sessionId || 'default_session',
        message_content: message.content,
        agent_id: id,
        is_like: isLike
      });

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/comments`, {
        message_id: messageId,
        session_id: message.sessionId || 'default_session',
        message_content: message.content,
        agent_id: id,
        is_like: isLike
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Update the like state for this message
        setMessageLikes(prev => ({
          ...prev,
          [messageId]: isLike
        }));
        console.log('Like/dislike successful');
      } else {
        console.error('API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Error sending like/dislike:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleDislike = async (messageId) => {
    setCurrentMessageId(messageId);
    setCommentContent('');
    setShowCommentModal(true);
  };

  const handleSubmitComment = async () => {
    if (!commentContent.trim() || !currentMessageId) {
      console.error('Missing comment content or message ID');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Find the message to get its content and session_id
      const message = chatMessages.find(msg => msg.originalId === currentMessageId);
      if (!message) {
        console.error('Message not found for comment submission, currentMessageId:', currentMessageId);
        console.log('Available messages:', chatMessages.map(m => ({ id: m.id, originalId: m.originalId })));
        return;
      }

      console.log('Submitting comment:', {
        message_id: currentMessageId,
        session_id: message.sessionId || 'default_session',
        message_content: message.content,
        agent_id: id,
        comment_content: commentContent.trim(),
        is_like: false
      });

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/comments`, {
        message_id: currentMessageId,
        session_id: message.sessionId || 'default_session',
        message_content: message.content,
        agent_id: id,
        comment_content: commentContent.trim(),
        is_like: false
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Update the like state for this message
        setMessageLikes(prev => ({
          ...prev,
          [currentMessageId]: false
        }));
        
        // Close modal and reset
        setShowCommentModal(false);
        setCommentContent('');
        setCurrentMessageId(null);
        console.log('Comment submitted successfully');
      } else {
        console.error('API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleCancelComment = () => {
    setShowCommentModal(false);
    setCommentContent('');
    setCurrentMessageId(null);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-blue-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/ai-agents')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white text-2xl">
                  {agent.avatar}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-black">{agent.name}</h1>
                  <p className="text-gray-600">{agent.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {agent.status}
              </span>
              
              <button
                onClick={() => setShowWhatsAppModal(true)}
                className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat Interface - Full Width */}
          <div className="w-full flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white">
                    {agent.avatar}
                  </div>
                  <div>
                    <h3 className="font-medium text-black">Chat with {agent.name}</h3>
                    <p className="text-sm text-gray-500">Last active: {agent.lastActive}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-sm text-gray-500">Online</span>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4" ref={chatMessagesRef}>
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                      msg.type === 'user'
                        ? 'bg-black text-white'
                        : msg.isError
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-black'
                    }`}
                  >
                    {/* Audio Message */}
                    {msg.audioUrl && (
                      <div className="mb-2">
                        <audio controls className="w-full">
                          <source src={msg.audioUrl} type={msg.mediaType || 'audio/mpeg'} />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}
                    
                    {/* Text Message */}
                    <div className="text-sm leading-relaxed">
                      {msg.type === 'agent' ? (
                        <div className="whitespace-pre-wrap">
                          {msg.content.split(/(\*\*.*?\*\*)/).map((part, index) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              // Extract the text between ** and make it bold
                              const boldText = part.slice(2, -2);
                              return <span key={index} className="font-bold">{boldText}</span>;
                            } else {
                              return <span key={index}>{part}</span>;
                            }
                          })}
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                      )}
                    </div>

                    {/* Like/Dislike Buttons for Agent Messages */}
                    {msg.type === 'agent' && !msg.isError && (
                      <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-gray-200">
                        <button
                          onClick={() => handleLike(msg.originalId, true)}
                          className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs transition-colors ${
                            messageLikes[msg.originalId] === true
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-green-50'
                          }`}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          <span>Like</span>
                        </button>
                        
                        <button
                          onClick={() => handleDislike(msg.originalId)}
                          className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs transition-colors ${
                            messageLikes[msg.originalId] === false
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-50'
                          }`}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span>Dislike</span>
                        </button>
                      </div>
                    )}

                    <p className={`text-xs mt-2 ${
                      msg.type === 'user' 
                        ? 'text-gray-200' 
                        : msg.isError
                        ? 'text-red-600'
                        : 'text-gray-500'
                    }`}>
                      {msg.timestamp}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-900 px-4 py-3 rounded-2xl">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">Typing...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                    rows="2"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!message.trim() || isLoading}
                  className="w-12 h-12 bg-black text-white rounded-full hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                >
                  {isLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-md w-full mx-4 transform transition-all">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.87 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Connect via WhatsApp</h3>
              </div>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="text-center mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Scan QR Code</h4>
                <p className="text-gray-600 mb-4">Scan this QR code with your WhatsApp to start chatting</p>
                
                {/* QR Code Placeholder */}
                <div className="w-48 h-48 bg-gray-100 rounded-lg mx-auto flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                    </svg>
                    <p className="text-sm text-gray-500">QR Code</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                Close
              </button>
              <button
                onClick={() => window.open('https://wa.me/212726998808', '_blank')}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.87 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span>Go to WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-md w-full mx-4 transform transition-all">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Add Comment</h3>
              </div>
              <button
                type="button"
                onClick={handleCancelComment}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please share your feedback about this response:
                </label>
                <textarea
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                  placeholder="Tell us what could be improved..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows="4"
                  autoFocus
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancelComment}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitComment}
                disabled={!commentContent.trim()}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                <span>Submit</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentDetail;
